import cx from 'clsx';
import { addMinutes, format } from 'date-fns';
import React from 'react';
import { useForm } from 'react-hook-form';
import { Tooltip } from 'react-tooltip';

import { ReactComponent as ClockIcon } from '@assets/svg/ic-clock.svg';
import { ReactComponent as InfoIcon } from '@assets/svg/ic-info.svg';

import ModalHeader from '@components/modals/ModalHeader/ModalHeader';
import Button from '@components/uikit/Button/Button';
import Input from '@components/uikit/Input/Input';
import Select from '@components/uikit/Select/Select';

import { MODAL_NAMES } from '@constants/modalRenderers';

import { showToast } from '@helpers/showToast';

import useTranslation from '@hooks/useTranslation';

import type { ILocationStructureNode } from '@model/structureData';

import type { IEquipmentItem } from '@pages/ManageEquipment/model/equipmentsList';
import { useEquipmentUpdateMutation } from '@pages/ManageEquipment/services/useEquipmentUpdateMutation';

import { useEquipmentTypesQuery } from '@services/equipment/useEquipmentTypesQuery';
import { useAssetTreeQuery } from '@services/useAssetTreeQuery';
import { useUserProfileQuery } from '@services/user/useUserProfileQuery';

import { selectAppRtl } from '@store/useAppStore';
import { useModalActions } from '@store/useModalStore';

import {
  EQUIPMENT_EDIT_FIELDS,
  equipmentEditDefaultValue,
  schemaEquipmentEdit,
} from './constants/schemaEquipmentEdit';
import style from './style.module.scss';

interface IProps {
  equipmentData: IEquipmentItem;
}

const EquipmentEditModal: React.FC<IProps> = ({ equipmentData }) => {
  const { t } = useTranslation();
  const { data: profileData } = useUserProfileQuery();
  const rtl = selectAppRtl();

  const { data: assetTreeData } = useAssetTreeQuery();
  const { setModalClose, setActiveModal } = useModalActions();

  const currentDateFormat = `${profileData?.dateFormat.date} ${profileData?.dateFormat.time}`;

  const {
    control,
    reset,
    handleSubmit,
    formState: { isSubmitting },
  } = useForm({
    mode: 'onChange',
    resolver: schemaEquipmentEdit,
    defaultValues: equipmentEditDefaultValue(equipmentData),
  });

  const { data: equipmentTypes = [], isLoading: equipmentTypesLoading } = useEquipmentTypesQuery({
    equipmentType: equipmentData.equipment_type || profileData?.customer.customer_id,
  });

  const getEquipmentStatus = () => {
    const { deactivateTable, is_deactivated } = equipmentData;
    if (!deactivateTable) {
      return 'active';
    }
    if (deactivateTable && !is_deactivated) {
      return 'scheduled';
    }
    return 'inactive';
  };

  const equipmentStatus = getEquipmentStatus();

  const handleClose = () => {
    setModalClose();
    reset();
  };

  const { mutate: equipmentUpdateMutate } = useEquipmentUpdateMutation();

  const submit = handleSubmit(
    (data) =>
      equipmentUpdateMutate(
        {
          id: equipmentData.id,
          asset_code: data.asset_code,
          location_id: data.location.id,
          name: data.equipment_name,
          equipment_type_id: data.equipment_type.id,
        },
        {
          onSuccess: () => {
            showToast({ variant: 'success', message: t('Your changes have been saved.') });

            handleClose();
          },
        },
      ),
    (errors) => console.error(errors),
  );

  const getItemLabel = (item: ILocationStructureNode) => {
    const offset = item.repeats || 0;
    return <div style={{ marginLeft: 3 * offset }}>{item.name}</div>;
  };

  const filteredAvailableLocations = assetTreeData?.locations.filter(
    (loc) => +loc.facilityId === equipmentData.locationTable.facility_id,
  );

  return (
    <div className={cx(style.equipmentEditModal, { 'body-rtl': rtl })}>
      <ModalHeader title={t('Edit Equipment')} />
      <div className={style.modalBody}>
        <div className={style.twoParts}>
          <Input
            name={EQUIPMENT_EDIT_FIELDS.FACILITY_NAME}
            control={control}
            label={t('Facility')}
            disabled
          />
          <div className={style.locationSelectWrapper}>
            <Select
              name={EQUIPMENT_EDIT_FIELDS.LOCATION}
              control={control}
              label={t('Location')}
              options={filteredAvailableLocations || []}
              labelRender={(item) => getItemLabel(item)}
            />
            <Button
              className={style.createLocBtn}
              type="secondary"
              onClick={() =>
                setActiveModal({
                  name: MODAL_NAMES.createLocationModal,
                  options: {
                    onClose: () =>
                      setActiveModal({ name: MODAL_NAMES.equipmentEditModal, options: { equipmentData } }),
                  },
                })
              }
              text={`+ ${t('Create Location')}`}
            />
          </div>
        </div>
        <div className={style.equipmentInfo}>
          <Input
            name={EQUIPMENT_EDIT_FIELDS.EQUIPMENT_NAME}
            control={control}
            label={t('Equipment Name')}
            required={true}
          />
          <div className={cx(style.equipmentStatus, style[`${equipmentStatus}Status`])}>
            {equipmentStatus}
          </div>
        </div>
        <div className={style.twoParts}>
          <Select
            name={EQUIPMENT_EDIT_FIELDS.EQUIPMENT_TYPE}
            control={control}
            label={t('Equipment Type')}
            options={equipmentTypes}
            searchable={true}
            loadingList={equipmentTypesLoading}
            itemKeys={{ valueFieldKey: 'id', labelFieldKey: 'name' }}
          />
          <Input name={EQUIPMENT_EDIT_FIELDS.ASSET_CODE} control={control} label={t('Asset Code')} />
        </div>
        {!!equipmentData?.deactivateTable && (
          <div>
            <label className={style.fieldLabel}>
              {t('Deactivation Period')}
              <InfoIcon id="info-icon" className={style.infoIcon} />
            </label>
            <div className={style.twoParts}>
              <div className={style.deactivationDate}>
                <ClockIcon />
                {format(
                  addMinutes(equipmentData.deactivateTable?.start_date, equipmentData.facility_offset * 60),
                  currentDateFormat,
                )}
              </div>
              <div className={style.deactivationDate}>
                <ClockIcon className={style.deactivateClock} />
                {equipmentData.deactivateTable?.end_date
                  ? format(
                      addMinutes(equipmentData.deactivateTable?.end_date, equipmentData.facility_offset * 60),
                      currentDateFormat,
                    )
                  : '-- --'}
              </div>
            </div>
          </div>
        )}
      </div>
      <div className={style.modalFooter}>
        <Button type="secondary" text={t('Cancel')} onClick={handleClose} />
        <Button type="primary" variant={'medium'} disabled={isSubmitting} text={t('Save')} onClick={submit} />
      </div>
      <Tooltip anchorSelect="#info-icon" content={t('Original time based on facility time zone')} />
    </div>
  );
};

export default EquipmentEditModal;
